<template>
    <div class="lsva-dashboard-vehicles-table">
        <div class="l-inline l-center-v l-gap-2 filter-actions">
            <div>{{ $tc('vehicles', vehicles.length) }}</div>

            <div class="l-grow-1 l-inline l-justify-end">
                <slot name="filter" />
            </div>

            <IconButton
                :disabled="!vehicles.length"
                :title="$t('export')"
                @click="$emit('export')"
            >
                <FileDownloadIcon width="18" height="18" />
            </IconButton>
        </div>

        <InfiniteScroller #default="{ listPortion }" :list="vehiclesSorted">
            <div v-if="isLoading" class="l-padded">
                <VSpinner
                    size="medium"
                    :line-fg-color="spinnerColor"
                    :speed="1"
                />
            </div>

            <div
                v-else-if="!vehicles.length"
                class="l-padded l-inline l-center t-subtle t-small"
            >
                <span>
                    {{ $t('shared.noSearchResult') }}
                </span>
            </div>

            <table v-else>
                <thead>
                    <tr>
                        <th v-for="column in columns" :key="column.key">
                            {{ column.label }}

                            <IconButton @click="handleSort(column.key)">
                                <SortArrowIcon
                                    :direction="
                                        sortBy === column.key
                                            ? sortDirection
                                            : 0
                                    "
                                    width="10"
                                    height="10"
                                />
                            </IconButton>
                        </th>
                    </tr>
                </thead>

                <template v-for="item in listPortion">
                    <tr :key="item.vin">
                        <td>{{ item.name }}</td>

                        <td>{{ item.vrn }}</td>

                        <td>
                            <Pill
                                v-if="item.transport_group"
                                :variant="
                                    getTransportGroupVariant(
                                        item.transport_group
                                    )
                                "
                            >
                                {{ item.transport_group }}
                            </Pill>
                        </td>

                        <td class="is-numeric">
                            {{ formatNumber(item.distance_assessed) }}
                        </td>

                        <td class="is-numeric">
                            {{ formatNumber(item.ton_kilometers) }}
                        </td>

                        <td
                            class="is-numeric"
                            :class="{ 'is-saving': item.amount_saved > 0 }"
                            :title="
                                item.amount_saved > 0
                                    ? $t('saved', {
                                          amount: formatNumber(
                                              item.amount_saved
                                          ),
                                      })
                                    : null
                            "
                        >
                            {{ formatNumber(item.amount_total) }}
                        </td>
                    </tr>
                </template>
            </table>
        </InfiniteScroller>
    </div>
</template>

<script>
import VSpinner from 'vue-simple-spinner'

import FileDownloadIcon from '@/components/icons/FileDownloadIcon'
import IconButton from '@/components/IconButton'
import InfiniteScroller from '@/components/InfiniteScroller'
import Pill from '@/components/redesigned/Pill'
import SortArrowIcon from '@/components/icons/SortArrowIcon'

const pillVariants = ['success', 'warning', 'primary', 'default']

export default {
    name: 'LsvaDashboardVehiclesTable',
    components: {
        FileDownloadIcon,
        IconButton,
        InfiniteScroller,
        Pill,
        SortArrowIcon,
        VSpinner,
    },
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        vehicles: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            sortBy: 'amount_total',
            sortDirection: -1,
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
        }
    },
    computed: {
        columns() {
            return [
                { key: 'name', label: this.$t('columns.vehicle') },
                { key: 'vrn', label: this.$t('columns.licensePlate') },
                {
                    key: 'transport_group',
                    label: this.$t('columns.transportGroup'),
                },
                {
                    key: 'distance_assessed',
                    label: this.$t('columns.distance'),
                },
                {
                    key: 'ton_kilometers',
                    label: this.$t('columns.tonKilometers'),
                },
                { key: 'amount_total', label: this.$t('columns.amount') },
            ]
        },
        transportGroups() {
            return [
                ...new Set(this.vehicles.map(item => item.transport_group)),
            ].sort()
        },
        vehiclesSorted() {
            return [...this.vehicles].sort((a, b) => {
                const aValue = a[this.sortBy]
                const bValue = b[this.sortBy]

                if (aValue === bValue) {
                    return 0
                } else if (aValue == null) {
                    return 1
                } else if (bValue == null) {
                    return -1
                } else if (typeof aValue === 'string') {
                    return (
                        aValue.localeCompare(bValue, undefined, {
                            numeric: true,
                        }) * this.sortDirection
                    )
                }

                return aValue > bValue
                    ? this.sortDirection
                    : -this.sortDirection
            })
        },
    },
    methods: {
        formatNumber(num) {
            return num.toLocaleString('de-CH', {
                maximumFractionDigits: 2,
            })
        },
        getTransportGroupVariant(group) {
            return pillVariants[
                this.transportGroups.indexOf(group) % pillVariants.length
            ]
        },
        handleSort(column) {
            if (this.sortBy !== column || this.sortDirection !== 1) {
                this.sortBy = column
                this.sortDirection = 1
            } else if (this.sortDirection === 1) {
                this.sortDirection = -1
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "columns": {
            "amount": "LSVA CHF",
            "distance": "km",
            "licensePlate": "License plate",
            "tonKilometers": "tkm",
            "transportGroup": "Transport group",
            "vehicle": "Vehicle"
        },
        "export": "CSV Export",
        "saved": "CHF {amount} saved",
        "vehicles": "{n} vehicle | {n} vehicles"
    },
    "de": {
        "columns": {
            "amount": "LSVA CHF",
            "distance": "km",
            "licensePlate": "Kennzeichen",
            "tonKilometers": "tkm",
            "transportGroup": "Bereich",
            "vehicle": "Fahrzeug"
        },
        "export": "CSV Export",
        "saved": "CHF {amount} eingespart",
        "vehicles": "{n} Fahrzeug | {n} Fahrzeuge"
    },
    "fr": {
        "columns": {
            "amount": "RPLP CHF",
            "distance": "km",
            "licensePlate": "Plaque d'immatriculation",
            "tonKilometers": "tkm",
            "transportGroup": "Secteur",
            "vehicle": "Véhicule"
        },
        "export": "Exporter CSV",
        "saved": "CHF {amount} économisés",
        "vehicles": "{n} véhicule | {n} véhicules"
    },
    "it": {
        "columns": {
            "amount": "TTPCP CHF",
            "distance": "km",
            "licensePlate": "Targa",
            "tonKilometers": "tkm",
            "transportGroup": "Settore",
            "vehicle": "Veicolo"
        },
        "export": "CSV Export",
        "saved": "CHF {amount} risparmiati",
        "vehicles": "{n} veicolo | {n} veicoli"
    }
}
</i18n>

<style lang="scss" scoped>
$filter-actions-height: 64px;

.filter-actions {
    position: sticky;
    top: 0;
    left: 0;
    padding: 0.75rem 2rem;
    height: $filter-actions-height;
    border-top: $style-border;
    border-bottom: $style-border;
    background-color: #fff;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.7);
    z-index: 11;
}

table {
    display: block;
    width: 100%;
    border-spacing: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.7);

    thead {
        position: sticky;
        top: $filter-actions-height;
        background-color: #fff;
        z-index: 10;

        .icon-button {
            margin-left: 5px;
        }
    }

    tr {
        &:not(:first-of-type) {
            td {
                border-top: $style-border;
            }
        }

        th {
            text-align: left;
            border-bottom: $style-border;
        }

        td {
            &.is-numeric {
                font-variant-numeric: tabular-nums;
            }

            &.is-saving {
                color: $color-green;
                font-weight: 600;
            }
        }

        th,
        td {
            width: 1%;
            padding: 1rem;
            white-space: nowrap;

            &:first-child {
                padding-left: 2rem;
            }

            &:last-child {
                padding-right: 2rem;
            }
        }
    }
}
</style>
